import { Box, Card, Divider, Grid, Stack, Typography } from '@mui/material';
import { formatClutchDate } from 'src/utils/formatDate';

function cleanText(value) {
  if (value == null) {
    return null;
  }
  const text = String(value).trim();
  if (!text || text === 'undefined') {
    return null;
  }
  return text;
}

function sessionText(session, snakeKey, camelKey) {
  return cleanText(session[snakeKey] ?? session[camelKey]);
}

export function formatSessionOvers(overs) {
  if (overs == null || overs === '') {
    return null;
  }

  const value = Number(overs);
  if (Number.isNaN(value)) {
    return null;
  }

  const text = Number.isInteger(value) ? String(value) : value.toFixed(1);
  return `${text} ${value === 1 ? 'Over' : 'Overs'}`;
}

function formatScore(value, outOf = 10) {
  if (value == null) {
    return '—';
  }
  return `${value}/${outOf}`;
}

function FieldLine({ label, value, placeholder = 'None', valueSx = {} }) {
  const hasValue = cleanText(value);
  const display = hasValue || placeholder;

  return (
    <Typography
      component="div"
      sx={{
        fontSize: '0.62rem',
        lineHeight: 1.35,
        mt: 0.35
      }}
    >
      <Box
        component="span"
        sx={{
          color: '#6B7280',
          fontWeight: 700,
          letterSpacing: '0.03em',
          display: 'block',
          fontSize: '0.55rem',
          mb: 0.1
        }}
      >
        {label}
      </Box>
      <Box
        component="span"
        sx={{
          color: hasValue ? '#F3F4F6' : '#6B7280',
          fontWeight: hasValue ? 600 : 400,
          fontStyle: hasValue ? 'normal' : 'italic',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          ...valueSx
        }}
        title={hasValue || undefined}
      >
        {display}
      </Box>
    </Typography>
  );
}

function MetricColumn({
  title,
  ratingLabel = 'Rating',
  ratingValue,
  detailLabel,
  detailValue,
  detailPlaceholder = 'None',
  singleLabel,
  singleValue,
  singlePlaceholder = 'None'
}) {
  return (
    <Box sx={{ flex: 1, minWidth: 0, px: { xs: 0, sm: 0.5 } }}>
      <Typography
        sx={{
          color: '#3F90DF',
          fontSize: '0.55rem',
          fontWeight: 700,
          letterSpacing: '0.06em',
          lineHeight: 1.2,
          mb: 0.15
        }}
      >
        {title}
      </Typography>
      {singleLabel ? (
        <FieldLine label={singleLabel} value={singleValue} placeholder={singlePlaceholder} />
      ) : (
        <>
          <FieldLine
            label={ratingLabel}
            value={ratingValue}
            placeholder="—"
            valueSx={{ fontWeight: 800, fontSize: '0.78rem' }}
          />
          {detailLabel && (
            <FieldLine label={detailLabel} value={detailValue} placeholder={detailPlaceholder} />
          )}
        </>
      )}
    </Box>
  );
}

function DiaryLine({ label, text, lineClamp = 2, placeholder = 'None' }) {
  const value = cleanText(text);
  const display = value || placeholder;

  return (
    <Typography
      component="div"
      sx={{
        fontSize: '0.62rem',
        lineHeight: 1.35
      }}
    >
      <Box component="span" sx={{ color: '#6B7280', fontWeight: 700, letterSpacing: '0.04em' }}>
        {label}
      </Box>
      <Box
        component="span"
        sx={{
          ml: 0.5,
          color: value ? '#9CA3AF' : '#6B7280',
          fontStyle: value ? 'normal' : 'italic',
          display: value ? '-webkit-box' : 'inline',
          WebkitLineClamp: lineClamp,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}
        title={value || undefined}
      >
        {display}
      </Box>
    </Typography>
  );
}

const cardSx = {
  bgcolor: '#151921',
  border: '1px solid #232935',
  borderRadius: 1.5,
  boxShadow: '0 1px 0 rgba(63, 144, 223, 0.08)'
};

export default function PlayerSessionCard({ session }) {
  const typeLabel = (session.session_type_label || session.session_type || 'Session').toUpperCase();
  const dateLabel = (
    session.session_date_label || formatClutchDate(session.session_date)
  ).toUpperCase();
  const balls = session.balls_bowled ?? '—';
  const oversText = formatSessionOvers(session.overs);

  const effortOutOf = session.effort_out_of ?? 10;

  const effortScore = formatScore(session.effort, effortOutOf);
  const effortCaption = cleanText(session.effort_label);

  const exhaustionLabel = sessionText(
    session,
    'post_session_exhaustion_label',
    'postSessionExhaustionLabel'
  );
  const exhaustionKey = sessionText(session, 'post_session_exhaustion', 'postSessionExhaustion');
  const frictionOutOf = session.physical_friction_out_of ?? 10;
  const legacyFrictionScore =
    session.physical_friction != null ? formatScore(session.physical_friction, frictionOutOf) : null;
  const exhaustionRating = exhaustionLabel || exhaustionKey || legacyFrictionScore || '—';

  const otherDemandsLabel =
    cleanText(session.other_demands_label) ||
    cleanText(session.other_demands_short_label) ||
    cleanText(session.other_demands);

  const exhaustionDescription =
    sessionText(session, 'post_session_exhaustion_description', 'postSessionExhaustionDescription') ||
    sessionText(session, 'session_exhaustion_description', 'sessionExhaustionDescription');
  const sessionNiggles = sessionText(session, 'session_niggles', 'sessionNiggles');
  const notes = sessionText(session, 'player_notes', 'playerNotes');

  return (
    <Card sx={cardSx} elevation={0}>
      <Box sx={{ px: 1.15, py: 1 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1}
          sx={{ mb: 0.5 }}
        >
          <Typography
            sx={{
              color: '#3F90DF',
              fontSize: '0.62rem',
              fontWeight: 700,
              letterSpacing: '0.06em',
              lineHeight: 1.25
            }}
          >
            {typeLabel} • {dateLabel}
          </Typography>
          <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ flexShrink: 0 }}>
            <Typography sx={{ color: '#6B7280', fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.06em' }}>
              DAILY LOAD
            </Typography>
            <Typography sx={{ color: '#F3F4F6', fontSize: '1rem', fontWeight: 800, lineHeight: 1 }}>
              {session.daily_load ?? '—'}
            </Typography>
          </Stack>
        </Stack>

        <Typography sx={{ color: '#F3F4F6', fontWeight: 800, fontSize: '0.92rem', lineHeight: 1.2, mb: 0.75 }}>
          {balls}{' '}
          <Box component="span" sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#9CA3AF' }}>
            BALLS
          </Box>
          {oversText && (
            <Box component="span" sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#6B7280', ml: 0.35 }}>
              ({oversText})
            </Box>
          )}
        </Typography>

        <Stack
          direction="row"
          divider={<Divider flexItem orientation="vertical" sx={{ borderColor: '#232935', my: 0.25 }} />}
          sx={{
            py: 0.5,
            px: 0.25,
            borderRadius: 1,
            bgcolor: 'rgba(11, 14, 20, 0.45)',
            border: '1px solid #232935'
          }}
        >
          <MetricColumn
            title="EFFORT"
            ratingValue={effortScore}
            detailLabel="Intensity"
            detailValue={effortCaption}
            detailPlaceholder="None"
          />
          <MetricColumn
            title="SESSION EXHAUSTION"
            ratingLabel="Rating"
            ratingValue={exhaustionRating}
            detailLabel="Description"
            detailValue={exhaustionDescription}
            detailPlaceholder="None"
          />
          <MetricColumn
            title="OTHER DEMANDS"
            singleLabel="Demand type"
            singleValue={otherDemandsLabel}
            singlePlaceholder="None"
          />
        </Stack>

        <Stack spacing={0.45} sx={{ mt: 0.65, pt: 0.5, borderTop: '1px solid #232935' }}>
          <Grid container spacing={0.75}>
            <Grid item xs={12} md={6}>
              <DiaryLine label="Session niggles" text={sessionNiggles} lineClamp={2} placeholder="None" />
            </Grid>
            <Grid item xs={12} md={6}>
              <DiaryLine label="Context diary notes" text={notes} lineClamp={2} placeholder="None" />
            </Grid>
          </Grid>
        </Stack>
      </Box>
    </Card>
  );
}
